import { useEffect, useState } from "react";
import { Plus, X, Save, Play, Zap, Wand2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { crmApi, formatDate, logDevError } from "../../lib/crm";
import { useCRMSettings } from "../../context/CRMSettingsContext";
import { toast } from "sonner";

export default function CRMSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [automations, setAutomations] = useState(null);
  const [running, setRunning] = useState("");
  const { refresh: refreshSettingsContext } = useCRMSettings();

  useEffect(() => {
    crmApi.get("/settings").then((r) => setSettings(r.data)).catch((err) => logDevError("CRMSettings.settings", err));
    crmApi.get("/automations/status").then((r) => setAutomations(r.data)).catch((err) => logDevError("CRMSettings.automations", err));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await crmApi.put("/settings", settings);
      toast.success("Settings saved");
      refreshSettingsContext();
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const runJob = async (id) => {
    setRunning(id);
    try {
      const r = await crmApi.post(`/automations/run/${id}`);
      toast.success(`${id} OK · ${JSON.stringify(r.data.result)}`);
      const status = await crmApi.get("/automations/status");
      setAutomations(status.data);
    } catch {
      toast.error("Job failed");
    } finally {
      setRunning("");
    }
  };

  const [backfilling, setBackfilling] = useState(false);
  const runBackfill = async () => {
    if (!window.confirm("Fill in 'country' for leads with empty country but a known city/area?")) return;
    setBackfilling(true);
    try {
      const r = await crmApi.post("/leads/backfill-countries");
      const lines = Object.entries(r.data.by_country || {}).map(([k, n]) => `${n} → ${k}`).join(", ");
      toast.success(`${r.data.updated} leads updated${lines ? " · " + lines : ""}`);
    } catch {
      toast.error("Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  if (!settings) return <div className="p-8 text-white/40 text-sm">Loading…</div>;

  const STAGE_IDS = ["new", "contacting", "no_response", "call_back", "contacted", "meeting_scheduled", "qualified", "proposal_sent", "negotiation", "won", "not_interested", "lost"];
  const stageLabelES = settings.stage_labels_es || {};
  const stageLabelEN = settings.stage_labels_en || {};
  const updateStageLabel = (lang, stageId, value) => {
    const key = lang === "es" ? "stage_labels_es" : "stage_labels_en";
    setSettings({ ...settings, [key]: { ...(settings[key] || {}), [stageId]: value } });
  };

  const JOB_META = {
    daily_agent_summary: { label: "Daily agent summary email", desc: "Sends each agent their tasks for today (08:00 UTC daily)" },
    birthday_and_inactivity: { label: "Birthday + inactivity alerts", desc: "In-app notifications for birthdays and inactive leads (09:00 UTC daily)" },
    auto_archive_lost: { label: "Auto-archive lost leads", desc: "Archives lost leads older than the configured threshold (03:00 UTC daily)" },
    weekly_admin_summary: { label: "Weekly admin summary", desc: "Sends admin a weekly recap email (Mon 09:00 UTC)" },
  };

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-3xl">
      <header>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-sm text-white/40 mt-1">Configure pipeline metadata and automation thresholds</p>
      </header>

      <ListEditor
        label="Industries"
        items={settings.industries}
        onChange={(v) => setSettings({ ...settings, industries: v })}
        testid="settings-industries"
      />
      <ListEditor
        label="Lead sources"
        items={settings.lead_sources}
        onChange={(v) => setSettings({ ...settings, lead_sources: v })}
        testid="settings-sources"
      />
      <ListEditor
        label="Lost reasons"
        items={settings.lost_reasons}
        onChange={(v) => setSettings({ ...settings, lost_reasons: v })}
        testid="settings-lost-reasons"
      />

      <div className="glass-strong rounded-2xl p-6 border border-white/8 grid sm:grid-cols-2 gap-4">
        <NumField
          label="Inactivity alert (days)"
          value={settings.inactivity_days}
          onChange={(v) => setSettings({ ...settings, inactivity_days: v })}
          testid="settings-inactivity-days"
          hint="Alert when a lead has no activity for X days"
        />
        <NumField
          label="Auto-archive lost (days)"
          value={settings.auto_archive_lost_days}
          onChange={(v) => setSettings({ ...settings, auto_archive_lost_days: v })}
          testid="settings-archive-days"
          hint="Auto-archive lost leads after X days (Phase 3 enforces)"
        />
      </div>

      {/* Pipeline stage labels */}
      <div className="glass-strong rounded-2xl p-6 border border-white/8" data-testid="settings-stage-labels">
        <h3 className="text-sm font-semibold text-white/80 mb-1">Pipeline stage labels</h3>
        <p className="text-xs text-white/40 mb-4">
          Customize how each pipeline stage appears across the CRM. Leave blank to use the default name.
        </p>
        <div className="grid grid-cols-3 gap-3 text-xs items-center">
          <div className="font-bold uppercase tracking-wider text-white/40">Stage</div>
          <div className="font-bold uppercase tracking-wider text-white/40">Label (ES)</div>
          <div className="font-bold uppercase tracking-wider text-white/40">Label (EN)</div>
          {STAGE_IDS.map((sid) => (
            <div key={sid} className="contents">
              <div className="text-white/60 mono">{sid}</div>
              <Input
                value={stageLabelES[sid] || ""}
                onChange={(e) => updateStageLabel("es", sid, e.target.value)}
                data-testid={`stage-label-es-${sid}`}
                placeholder="—"
                className="bg-white/5 border-white/10 text-white text-sm"
              />
              <Input
                value={stageLabelEN[sid] || ""}
                onChange={(e) => updateStageLabel("en", sid, e.target.value)}
                data-testid={`stage-label-en-${sid}`}
                placeholder="—"
                className="bg-white/5 border-white/10 text-white text-sm"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Data tools */}
      <div className="glass-strong rounded-2xl p-6 border border-white/8" data-testid="settings-data-tools">
        <div className="flex items-center gap-2 mb-4">
          <Wand2 className="w-4 h-4 text-lime-400" />
          <h3 className="text-sm font-semibold text-white/80">Data tools</h3>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 py-2">
          <div className="flex-1 min-w-[200px]">
            <div className="text-sm text-white/80 font-medium">Backfill lead countries</div>
            <p className="text-xs text-white/40 mt-1">
              Fills in <code>country</code> for leads where it&apos;s empty but the city/area is recognizable (Dubai → UAE, Madrid → España, etc.). Needed so the agent top-bar shows the country clock.
            </p>
          </div>
          <Button
            onClick={runBackfill}
            disabled={backfilling}
            data-testid="backfill-countries-btn"
            variant="outline"
            className="border-lime-400/30 text-lime-300 hover:bg-lime-400/10"
          >
            {backfilling ? "Running…" : "Run backfill"}
          </Button>
        </div>
      </div>

      {/* Automations */}
      <div className="glass-strong rounded-2xl p-6 border border-white/8" data-testid="settings-automations">
        <div className="flex items-center gap-2 mb-4">
          <Zap className="w-4 h-4 text-amber-400" />
          <h3 className="text-sm font-semibold text-white/80">Scheduled automations</h3>
          {automations?.running && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">
              ● Running
            </span>
          )}
        </div>
        <p className="text-xs text-white/40 mb-4">
          Jobs run automatically per their schedule. You can also trigger any of them manually below.
        </p>
        <div className="space-y-2">
          {(automations?.jobs || []).map((j) => {
            const meta = JOB_META[j.id] || {};
            return (
              <div
                key={j.id}
                data-testid={`job-${j.id}`}
                className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/8"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{meta.label || j.id}</div>
                  <div className="text-[11px] text-white/40">{meta.desc}</div>
                  <div className="text-[10px] text-white/30 mono mt-1">
                    Next run: {j.next_run ? formatDate(j.next_run) + " UTC" : "—"}
                  </div>
                </div>
                <Button
                  onClick={() => runJob(j.id)}
                  disabled={running === j.id}
                  data-testid={`run-job-${j.id}`}
                  variant="outline"
                  size="sm"
                  className="border-white/10 text-white/80 shrink-0"
                >
                  <Play className="w-3 h-3 mr-1" />
                  {running === j.id ? "Running…" : "Run now"}
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="sticky bottom-4 flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="settings-save-btn"
          className="bg-lime-400 hover:bg-lime-300 text-white shadow-xl"
        >
          <Save className="w-4 h-4 mr-1" />
          {saving ? "Saving…" : "Save settings"}
        </Button>
      </div>
    </div>
  );
}

function ListEditor({ label, items, onChange, testid }) {
  const [newItem, setNewItem] = useState("");
  const add = () => {
    const v = newItem.trim();
    if (!v) return;
    if (items.includes(v)) return;
    onChange([...items, v]);
    setNewItem("");
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div className="glass-strong rounded-2xl p-6 border border-white/8" data-testid={testid}>
      <h3 className="text-sm font-semibold text-white/80 mb-3">{label}</h3>
      <div className="flex flex-wrap gap-2 mb-3">
        {items.map((it, i) => (
          <span
            key={it}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-white/5 border border-white/10 rounded-full"
          >
            {it}
            <button
              onClick={() => remove(i)}
              data-testid={`${testid}-remove-${i}`}
              className="text-white/40 hover:text-red-400"
              aria-label={`Remove ${it}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder={`Add ${label.toLowerCase().slice(0, -1)}…`}
          data-testid={`${testid}-input`}
          className="bg-white/5 border-white/10 text-white text-sm"
        />
        <Button
          onClick={add}
          data-testid={`${testid}-add-btn`}
          variant="outline"
          className="border-white/10 text-white/80"
        >
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, testid, hint }) {
  return (
    <div>
      <Label className="text-white/70 text-xs">{label}</Label>
      <Input
        type="number"
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
        data-testid={testid}
        className="bg-white/5 border-white/10 text-white mt-1"
      />
      {hint && <p className="text-[11px] text-white/30 mt-1">{hint}</p>}
    </div>
  );
}
